import axios from 'axios';
import { CommonState, error, isFetching } from '@pec/aion-ui-core/slices/common';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enqueueRequestErrorSnackbar } from '@pec/aion-ui-core/actions/enqueueSnackbar';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';
import { ICompletePayload, ICompleteResponse } from '../surveyjs/common/types/complete';
import { IProductsResponse } from '../surveyjs/common/types/products';
import { ISubscription, SubscriptionStatus } from '../interfaces/subscriptions';
import { OdataComparator } from '@pec/aion-ui-odata/types/odataComparator';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { RootState } from '../combineReducers';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';

const { Equals } = OdataComparator;

const name = 'subscriptionsV2';

const fetchSubscriptionsRequest = (organizationId: string) => {
  const params = new QueryBuilder().filter(f => f.filterBy('subscriberId', Equals, organizationId)).toQueryParam();
  return axios.get<OdataResponse<ISubscription[]>>('/api/v3.1/subscriptions', { params });
};
export const fetchSubscriptions = createAsyncThunk(
  `${name}/fetch`,
  async (organizationId: string, { dispatch }) => {
    try {
      const { data } = await fetchSubscriptionsRequest(organizationId);
      return data;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { isFetching, subscriptions } = (getState() as RootState).subscriptionsV2;
      return !isFetching && subscriptions.length === 0;
    }
  }
);

export const refetchSubscriptions = createAsyncThunk(
  `${name}/refetch`,
  async (organizationId: string, { dispatch }) => {
    try {
      const { data } = await fetchSubscriptionsRequest(organizationId);
      return data;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

type ActivateFoundationSubscriptionParams = {
  organizationId: string;
  subscriptionId: string;
};
export const activateFoundationSubscription = createAsyncThunk(
  `${name}/activateFoundation`,
  async ({ organizationId, subscriptionId }: ActivateFoundationSubscriptionParams, { dispatch }) => {
    const {
      payload: {
        companyType,
        contactEmailAddress,
        applicationType,
        assessmentType,
        countryIso2Code,
        employeeCount: { employeeCount, subcontractorEmployeeCount },
        defaultOperatingCountry
      }
    } = await dispatch(fetchOrganizationInfo(organizationId));
    const {
      data: { products, taxRate }
    } = await axios.get<IProductsResponse>(
      `/api/v1/products?Location=${defaultOperatingCountry.iso2Code}&Country=${countryIso2Code}&NumOfEmployees=${employeeCount}&isFoundation=true`
    );
    const foundationProduct = products.find(product => product.subscriptionTier === SubscriptionTiers.Foundation);

    if (foundationProduct) {
      const body: ICompletePayload = {
        organizationId,
        subscriptionId,
        companyType,
        applicationType,
        assessmentType,
        operationType: 'Renewal',
        products: [foundationProduct.id],
        location: defaultOperatingCountry.iso2Code,
        subscriptionTier: SubscriptionTiers.Foundation,
        totalEmployeeCount: employeeCount,
        subcontractorEmployeeCount,
        taxRateId: taxRate ? taxRate.paymentProviderRateId : undefined,
        email: contactEmailAddress,
        successUrl: `${window.location.origin}/organizations/${organizationId}`,
        cancelUrl: `${window.location.origin}/organizations/${organizationId}`
      };
      const { data } = await axios.post<ICompleteResponse>(
        `/api/v1/organizations/${organizationId}/subscriptions/${subscriptionId}/checkout`,
        body
      );
      window.location.href = data.checkoutUrl;
    }
  }
);

type RequestRefundParams = {
  organizationId: string;
  subscriptionId: string;
  afterSuccess(): void;
};
export const requestRefund = createAsyncThunk(
  `${name}/refund`,
  async ({ organizationId, subscriptionId, afterSuccess }: RequestRefundParams, { dispatch }) => {
    try {
      await axios.post(
        `/api/v1/OrganizationSubscriptions/organizations/${organizationId}/subscriptions/${subscriptionId}/refund`
      );
      afterSuccess && afterSuccess();
      return;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export type State = CommonState & {
  subscriptions: ISubscription[];
};

const initialState: State = {
  subscriptions: [],
  isFetching: false,
  error: null
};

const subscriptionsV2 = createSlice({
  name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSubscriptions.pending, isFetching)
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.subscriptions = action.payload.value
          .filter(subscription => subscription.status === SubscriptionStatus.Active)
          .sort((a, b) => Number(b.isMain) - Number(a.isMain));
        state.isFetching = false;
      })
      .addCase(fetchSubscriptions.rejected, error)
      .addCase(refetchSubscriptions.pending, isFetching)
      .addCase(refetchSubscriptions.fulfilled, (state, action) => {
        state.subscriptions = action.payload.value
          .filter(subscription => subscription.status === SubscriptionStatus.Active)
          .sort((a, b) => Number(b.isMain) - Number(a.isMain));
        state.isFetching = false;
      })
      .addCase(refetchSubscriptions.rejected, error);
  }
});

export const { reducer: subscriptionsV2Reducer } = subscriptionsV2;
